import { NavigationExtras, Router, UrlTree } from '@angular/router';
import { ReceiptType, ReceiptsService } from '@services/dws/receipts.service';

export const ORDER_INFO_V1_SEGMENT = 'order-info';
export const ORDER_INFO_V2_SEGMENT = 'order-info-v2';

/** True for orders created by the v4 import (ecommerce / B2C). */
export function isV4ImportedOrder(order: { data?: unknown } | null | undefined): boolean {
  const data = order?.data;
  if (!data || typeof data !== 'object') {
    return false;
  }
  const record = data as Record<string, unknown>;
  const schema = record['schemaVersion'] ?? record['schema_version'];
  if (typeof schema === 'string' && schema.trim().length > 0) {
    return true;
  }
  const source = record['sourceExternalType'] ?? record['source_external_type'];
  return source === 'divinea-b2c';
}

/** POS / punto vendita: always open the new order detail. */
export type OrderInfoRouteSource = 'pos' | 'browse';

/** Minimal order identity for routing and receipt lookup (id may be string from events/API). */
export interface OrderInfoRouteKeys {
  id?: string | number;
  code?: string;
  externalId?: string | null;
}

function orderRouteId(order: OrderInfoRouteKeys): string | number {
  if (order.id !== undefined && order.id !== null) {
    return order.id;
  }
  throw new Error('OrderInfoRouteKeys.id is required for navigation');
}

/** Keys accepted by {@link ReceiptsService.getByEntity} for ORDER receipts. */
export function orderReceiptLookupKeys(order: OrderInfoRouteKeys): string[] {
  const out: string[] = [];
  const add = (v: string | number | undefined | null) => {
    if (v === undefined || v === null) {
      return;
    }
    const s = String(v).trim();
    if (s.length > 0 && !out.includes(s)) {
      out.push(s);
    }
  };
  add(order.code);
  add(order.externalId);
  add(order.id);
  return out;
}

/** True when at least one ORDER receipt exists for this order (new-detail version). */
export async function orderHasReceipt(
  receiptsService: ReceiptsService,
  order: OrderInfoRouteKeys
): Promise<boolean> {
  const keys = orderReceiptLookupKeys(order);
  if (!keys.length) {
    return false;
  }
  const seen = new Set<string>();
  for (const key of keys) {
    try {
      const list = await receiptsService.getByEntity(key, ReceiptType.ORDER).toPromise();
      for (const r of list ?? []) {
        if (r?.id) {
          seen.add(r.id);
        }
      }
      if (seen.size > 0) {
        return true;
      }
    } catch {
      /* try next key */
    }
  }
  return false;
}

export async function resolveOrderInfoSegment(
  receiptsService: ReceiptsService,
  source: OrderInfoRouteSource,
  order: OrderInfoRouteKeys
): Promise<string> {
  if (source === 'pos') {
    return ORDER_INFO_V2_SEGMENT;
  }
  const hasReceipt = await orderHasReceipt(receiptsService, order);
  return hasReceipt ? ORDER_INFO_V2_SEGMENT : ORDER_INFO_V1_SEGMENT;
}

export function orderInfoUrlTree(
  router: Router,
  orderId: string | number,
  segment: string,
  queryParams?: Record<string, string | boolean>
): UrlTree {
  return router.createUrlTree(['/orders', orderId, segment], { queryParams });
}

export async function navigateToOrderInfo(
  router: Router,
  receiptsService: ReceiptsService,
  order: OrderInfoRouteKeys,
  source: OrderInfoRouteSource,
  extras?: NavigationExtras
): Promise<boolean> {
  const segment = await resolveOrderInfoSegment(receiptsService, source, order);
  return router.navigate(['/orders', orderRouteId(order), segment], extras);
}

/** List, events, homepage, contacts: v2 only when the order has a receipt. */
export async function navigateToOrderInfoBrowse(
  router: Router,
  receiptsService: ReceiptsService,
  order: OrderInfoRouteKeys,
  extras?: NavigationExtras
): Promise<boolean> {
  return navigateToOrderInfo(router, receiptsService, order, 'browse', extras);
}

/** Punto vendita / POS flows: always order-info-v2. */
export function navigateToOrderInfoPos(
  router: Router,
  orderId: string | number,
  extras?: NavigationExtras
): Promise<boolean> {
  return router.navigate(['/orders', orderId, ORDER_INFO_V2_SEGMENT], extras);
}

export async function openOrderInfoBrowseInNewTab(
  router: Router,
  receiptsService: ReceiptsService,
  order: OrderInfoRouteKeys,
  queryParams?: Record<string, string | boolean>
): Promise<void> {
  const segment = await resolveOrderInfoSegment(receiptsService, 'browse', order);
  const url = router.serializeUrl(orderInfoUrlTree(router, orderRouteId(order), segment, queryParams));
  window.open(url, '_blank');
}
